/**
 * Best-effort parser for the AI-generated reading interpretation.
 *
 * The interpretation is free-form text produced by an LLM that loosely follows
 * a prompt asking for these blocks (emoji markers intentionally vary):
 *   ✨ Описание твоего расклада:   (umbrella title)
 *   ❓ <short answer>             (only when a question was asked)
 *   ✨ Карты / Описание карт / Значение карт:
 *      <emoji> <Card name> [(перевернутая)]
 *      <meaning…>
 *   🔗 / 🔄 / 🌀 Взаимодействие карт:
 *   🎯 Прогноз:
 *
 * We split by keywords (start-of-line, after stripping the leading emoji) and
 * blank lines, enforcing a monotonic section order so a stray keyword inside a
 * body never rewinds the parser. If the two anchor blocks (cards + forecast)
 * cannot be recovered, we return `null` and the caller falls back to rendering
 * the raw interpretation as a single block.
 */

const DEFAULT_CARD_EMOJI = String.fromCodePoint(0x1f0cf);

export interface ParsedCard {
  emoji: string;
  /** Card name, including a trailing "(перевернутая)" marker when present. */
  name: string;
  isReversed: boolean;
  /** Meaning text; may contain inline <b> tags. */
  meaning: string;
}

export interface ParsedInterpretation {
  /** Short answer / umbrella description (the ❓ block). */
  description: string | null;
  cards: ParsedCard[];
  interaction: string | null;
  forecast: string | null;
}

type Section = 'description' | 'cards' | 'interaction' | 'forecast';
type Stage = 'preamble' | 'title' | Section;

const STAGE_ORDER: Record<Stage, number> = {
  preamble: -1,
  title: 0,
  description: 1,
  cards: 2,
  interaction: 3,
  forecast: 4,
};

interface HeaderHit {
  section: Stage;
  /** Text found inline on the header line (e.g. "Прогноз: <inline>"). */
  inline: string;
}

/** Splits a leading emoji / symbol run from the textual remainder of a line. */
function splitEmoji(line: string): { emoji: string; text: string } {
  const noTags = line.replace(/<[^>]+>/g, '').replace(/\u00a0/g, ' ');
  const match = noTags.match(/^(\s*[^\p{L}\p{N}]*\s*)([\s\S]*)$/u);
  if (!match) return { emoji: '', text: noTags.trim() };
  return { emoji: match[1].replace(/\s+/g, ''), text: match[2].trim() };
}

function afterColon(text: string): string {
  const idx = text.indexOf(':');
  return idx >= 0 ? text.slice(idx + 1).trim() : '';
}

function detectHeader(line: string): HeaderHit | null {
  const hasQuestion = /[\u2753\u2754]/.test(line);
  const { text } = splitEmoji(line);
  const lc = text.toLowerCase();
  if (!lc && !hasQuestion) return null;

  // Umbrella title: "Описание твоего расклада" — carries no body itself.
  if (/^описание\s+(тво|раскл)/.test(lc)) return { section: 'title', inline: '' };

  // Short answer block, marked by the question emoji or an explicit lead-in.
  if (hasQuestion || /^(краткий ответ|короткий ответ|ответ на вопрос|ответ:)/.test(lc)) {
    return { section: 'description', inline: text };
  }

  if (/^взаимод/.test(lc)) return { section: 'interaction', inline: afterColon(text) };
  if (/^прогноз/.test(lc)) return { section: 'forecast', inline: afterColon(text) };

  // The cards header keyword ("карт…") also appears mid-sentence inside card
  // meanings, so additionally require a short, header-like line.
  const isHeaderLike = text.length <= 40;
  if (isHeaderLike && /^(карты|карта|карт|описание\s+карт|значение\s+карт)/.test(lc)) {
    return { section: 'cards', inline: '' };
  }

  return null;
}

/** Drops blank lines at both ends and joins the remainder with newlines. */
function joinParagraphs(lines: string[]): string {
  const trimmed = [...lines];
  while (trimmed.length && trimmed[0].trim() === '') trimmed.shift();
  while (trimmed.length && trimmed[trimmed.length - 1].trim() === '') trimmed.pop();
  return trimmed.join('\n').trim();
}

function parseCards(lines: string[]): ParsedCard[] {
  const chunks: string[][] = [];
  let current: string[] = [];
  for (const line of lines) {
    if (line.trim() === '') {
      if (current.length) {
        chunks.push(current);
        current = [];
      }
    } else {
      current.push(line);
    }
  }
  if (current.length) chunks.push(current);

  const cards: ParsedCard[] = [];
  for (const chunk of chunks) {
    const { emoji, text } = splitEmoji(chunk[0]);
    const name = text.trim();
    if (!name) continue;

    const meaning = joinParagraphs(chunk.slice(1));
    if (!meaning) continue;

    cards.push({
      emoji: emoji || DEFAULT_CARD_EMOJI,
      name,
      isReversed: /перевернут/iu.test(name),
      meaning,
    });
  }
  return cards;
}

export function parseInterpretation(raw: string): ParsedInterpretation | null {
  if (!raw || !raw.trim()) return null;

  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const buffers: Record<Section, string[]> = {
    description: [],
    cards: [],
    interaction: [],
    forecast: [],
  };

  let stage: Stage = 'preamble';

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/u, '');
    const header = detectHeader(line);

    if (header && STAGE_ORDER[header.section] >= STAGE_ORDER[stage]) {
      stage = header.section;
      if (header.section !== 'title' && header.section !== 'preamble' && header.inline) {
        buffers[header.section].push(header.inline);
      }
      continue;
    }

    if (stage !== 'preamble' && stage !== 'title') {
      buffers[stage].push(line);
    }
  }

  const description = joinParagraphs(buffers.description);
  const interaction = joinParagraphs(buffers.interaction);
  const forecast = joinParagraphs(buffers.forecast);
  const cards = parseCards(buffers.cards);

  // Anchor blocks required for a confident structured render.
  if (cards.length === 0 || !forecast) return null;

  return {
    description: description || null,
    cards,
    interaction: interaction || null,
    forecast,
  };
}
